import { useCallback, useEffect, useState } from "react";
import { BookingRewireDataType } from "../../../../Models/BookingRewireDataType";
import { BookingEntry, ResourceData } from "../types";
import { getInventoryIdFromEntry } from "../utils/eventsUtils";

const useResourceSelection = (
  booking: BookingRewireDataType | null,
  isOpen: boolean,
  initialSelectedResourceId?: string | null,
) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Initialize selection when modal opens
  useEffect(() => {
    if (!isOpen) {
      setSelectedIds(new Set());
      return;
    }

    if (!booking?.bookingEntries?.length) {
      setSelectedIds(new Set());
      return;
    }

    if (initialSelectedResourceId) {
      const hasMatch = booking.bookingEntries.some((entry: BookingEntry) =>
        getInventoryIdFromEntry(entry) === initialSelectedResourceId,
      );

      if (hasMatch) {
        setSelectedIds(new Set([initialSelectedResourceId]));
        return;
      }
    }

    // Default: select all resources
    const allIds = booking.bookingEntries.map((entry: BookingEntry) =>
      getInventoryIdFromEntry(entry)
    );
    setSelectedIds(new Set(allIds));
  }, [isOpen, booking, initialSelectedResourceId]);

  const toggleResource = useCallback((resourceId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback((resources: ResourceData[], selected: boolean) => {
    if (selected) {
      const allIds = resources.map((r) => r.id as string);
      setSelectedIds(new Set(allIds));
    } else {
      setSelectedIds(new Set());
    }
  }, []);

  const isSelected = useCallback(
    (resourceId: string) => selectedIds.has(resourceId),
    [selectedIds]
  );

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    toggleResource,
    selectAll,
    isSelected,
  };
};

export default useResourceSelection;
